import { useQueryClient, useIsFetching, Query } from "@tanstack/react-query";
import { isFunction } from "lodash-es";
import { useEffect, useState } from "react";

import { PREFIX_CACHE_KEY_LIST } from "src/eventsCatalogue/queries";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { EntityViewParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export enum EntityTabIds {
  Decisions = "decisions",
  Events = "events",
  Features = "features",
  RelatedEntities = "related-entities",
}

export const decisionsQueryKeys = [
  ["flows"],
  ["decision", "v3"],
  ["decision-overview"],
];

export const useEntityTabRefresh = (activeTab: EntityTabIds) => {
  const [isManualRefresh, setIsManualRefresh] = useState(false); // state to differentiate between manual refresh triggered by user and background refreshes/initial mount fetch
  const queryClient = useQueryClient();
  const { id: entityId, schema } = useParamsDecode<EntityViewParams>();
  const [environment] = useEnvironment();
  const { workspace } = useWorkspaceContext();

  const queryKeysMappings: Record<
    EntityTabIds,
    (string[] | ((query: Query) => boolean))[]
  > = {
    events: [
      ({ queryKey }) =>
        queryKey.includes(PREFIX_CACHE_KEY_LIST) &&
        queryKey.includes(workspace.id) &&
        queryKey.includes(entityId) &&
        queryKey.includes(environment),
    ],
    decisions: decisionsQueryKeys,
    features: [["features", workspace.id, environment, entityId, schema]],
    "related-entities": [["relatedEntities", schema]],
  };

  const queryKey = queryKeysMappings[activeTab];
  const filter = isFunction(queryKey[0])
    ? { predicate: queryKey[0] }
    : { queryKey: queryKey[0] };

  const isFetching = useIsFetching({
    ...filter,
    exact: false,
  });

  useEffect(() => {
    if (!isFetching && isManualRefresh) {
      setIsManualRefresh(false);
    }
  }, [isFetching, isManualRefresh]);

  const refresh = () => {
    setIsManualRefresh(true);
    queryKey.forEach((key) => {
      if (isFunction(key)) {
        queryClient.invalidateQueries({ predicate: key });
      } else {
        queryClient.invalidateQueries({ queryKey: key });
      }
    });
  };

  return {
    isRefreshing: isManualRefresh,
    refresh,
  };
};
